import { AdaptedLocation } from "adapters/LocationsAdapter";
import { useAuth } from "contexts/authContext";
import { useManualOccurrence } from "contexts/register/ManualOccurrence";
import { socket } from "hooks/useSocket";
import * as L from "leaflet";
import { markerIcon } from "components/Maps/MarkerIcon";
import { LatLngTuple } from "leaflet";
import OccurrenceInfo from "pages/Navigation/SharedOccurrence/OccurrenceInfo";
import { useEffect, useRef, useState } from "react";
import { Marker, TileLayer } from "react-leaflet";
import LocationsService, {
  GetLocationsParams,
} from "services/LocationsService";
import { fireToast } from "services/sweetAlertConfig";
import { PolicemanList, TenantList } from "utils/treatSocketLocations";
import Loader from "../../Loader";
import {
  Content,
  DetachedWrapper,
  ErrorMessage,
  OccurrenceInfoContainer,
  StyledMap,
  StyledMapContainer,
  Wrapper,
} from "./styles";
import NoPolicemanError from "../NoPolicemanError";
import { IReport } from "interfaces/Report";
import NoSelectedReportError from "../NoSelectedReportError";

export default function RouteViewTab() {
  const { occurrenceToEdit, selectedReport } = useManualOccurrence();

  const registeredPolicemanId = selectedReport?.policeman?.id;
  const detachedPolicemanId =
    selectedReport?.superficialPoliceman?.id;

  const [polyline, setPolyline] = useState<L.Polyline | null>(null);
  const [polylineLocations, setPolylineLocations] = useState<AdaptedLocation>(
    []
  );
  const [policemanLocation, setPolicemanLocation] = useState<LatLngTuple>();
  const [occurrenceLocation, setOccurrenceLocation] = useState<LatLngTuple>();
  const { tenant } = useAuth();
  const mapRef = useRef<L.Map>(null);

  async function fetchPolylineCoords(params: GetLocationsParams) {
    try {
      const polylineCoords = await LocationsService.getLocations(params);
      setPolylineLocations(polylineCoords);
    } catch {
      fireToast(
        "ATENÇÃO! O apoio ainda nao enviou nenhuma coordenada, portanto nao sera possivel visualizar o trajeto.",
        4000,
        "top-right"
      );
    }
  }

  useEffect(() => {
    const registeredPolicemanId = selectedReport?.policeman?.id;

    if (
      occurrenceToEdit !== undefined &&
      occurrenceToEdit.report &&
      registeredPolicemanId
    ) {
      const params: GetLocationsParams = {
        policemanId: registeredPolicemanId,
        reportId: selectedReport?.id,
      };

      fetchPolylineCoords(params);

      const occurrenceCoords: LatLngTuple = [
        Number(occurrenceToEdit.lastCoordX),
        Number(occurrenceToEdit.lastCoordY),
      ];

      setOccurrenceLocation(occurrenceCoords);

      mapRef.current?.flyTo(occurrenceCoords);
    }
  }, [occurrenceToEdit, selectedReport]);

  function getPolicemanLocation(objList: PolicemanList) {
    const registeredPolicemanId = selectedReport?.policeman?.id;

    if (registeredPolicemanId) {
      setPolicemanLocation([
        objList[registeredPolicemanId]?.coords?.latitude,
        objList[registeredPolicemanId]?.coords?.longitude,
      ]);
    }
  }

  function handleSocketLocationChange(locations: TenantList | PolicemanList) {
    const tenantId = tenant?.id;
    if (!tenantId) return;

    if (tenantId in locations) {
      getPolicemanLocation(locations[tenantId] as PolicemanList);
    } else {
      getPolicemanLocation(locations as PolicemanList);
    }
  }

  useEffect(() => {
    socket.on("onLocationChange", handleSocketLocationChange);

    if (tenant?.id) {
      socket.emit("listLocations", tenant?.id);
      socket.on("listLocations", handleSocketLocationChange);
    }

    return () => {
      socket.off("onLocationChange");
      socket.off("listLocations");
    };
  }, [occurrenceToEdit, tenant]);



  if (!selectedReport) {
    return (
      <Wrapper>
        <NoSelectedReportError />
      </Wrapper>
    )
  }

  if (selectedReport && detachedPolicemanId) {
    return (
      <DetachedWrapper>
        <ErrorMessage>
          O apoio associado a essa ocorrência é avulso. Não é possível
          visualizar o trajeto de um apoio sem registro no sistema.
        </ErrorMessage>
      </DetachedWrapper>
    );
  }

  return registeredPolicemanId ? (
    <Wrapper>
      <header>
        <h1>Visualizacao do trajeto</h1>
      </header>
      <Content>
        <OccurrenceInfoContainer>
          {occurrenceToEdit && (
            <OccurrenceInfo
              selectedReport={{} as IReport}
              occurrence={occurrenceToEdit}
              isTrackButtonDisabled={polylineLocations.length === 0}
              onTrackButtonClick={() => {
                setPolyline((prev) => {
                  if (!prev) {
                    const mapPolyline = L.polyline(polylineLocations, {
                      color: "#191919",
                    }).addTo(mapRef.current!);
                    return mapPolyline;
                  } else {
                    mapRef.current!.removeLayer(prev);
                    return null;
                  }
                });
              }}
            />
          )}
          {!occurrenceToEdit && <Loader color="white" size={90} />}
        </OccurrenceInfoContainer>

        <StyledMapContainer>
          {!occurrenceLocation && <Loader color="white" size={90} />}

          {occurrenceLocation && (
            <StyledMap center={occurrenceLocation} zoom={10} ref={mapRef}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {!!policemanLocation && (
                <Marker
                  icon={markerIcon({
                    color: "black",
                    type: "policemanOnDuty",
                  })}
                  position={{
                    lat: policemanLocation?.[0] ?? "0",
                    lng: policemanLocation?.[1] ?? "0",
                  }}
                  eventHandlers={{
                    click: () => { },
                  }}
                />
              )}

              {!!occurrenceLocation && (
                <Marker
                  icon={markerIcon({
                    color: "white",
                    type: "occPatrimony",
                  })}
                  position={{
                    lat: occurrenceLocation?.[0] ?? "0",
                    lng: occurrenceLocation?.[1] ?? "0",
                  }}
                  eventHandlers={{
                    click: () => { },
                  }}
                />
              )}
            </StyledMap>
          )}
        </StyledMapContainer>
      </Content>
    </Wrapper>
  ) : (
    <Wrapper>
      <NoPolicemanError />
    </Wrapper>
  );
}
